import type { ComponentProps } from 'react'

import type { IOption } from 'common/interfaces/IOption'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'

import type { EhrAppointmentModals } from './EhrAppointmentModals'
import type { useAppointmentActions } from './useAppointmentActions'
import type { useDoctorAwayCheck } from './useDoctorAwayCheck'
import type { usePatientPicker } from './usePatientPicker'
import type { RemovalConfirm } from './useRemovalConfirm'

type ModalsProps = ComponentProps<typeof EhrAppointmentModals>
type SetOpen = (open: boolean) => void

type Args = {
  languages: IOption[]
  isAddPatientOpen: boolean
  isNotifyOpen: boolean
  isDeleteOpen: boolean
  isEncounterOpen: boolean
  isManualCheckInOpen: boolean
  isOverlapOpen: boolean
  setIsAddPatientOpen: SetOpen
  setIsNotifyOpen: SetOpen
  setIsDeleteOpen: SetOpen
  setIsEncounterOpen: SetOpen
  setIsCheckInOpen: SetOpen
  setPendingOverlapSave: SetOpen
  editedPatientData?: IPatientSchedule
  patientPicker: ReturnType<typeof usePatientPicker>
  actions: ReturnType<typeof useAppointmentActions>
  awayCheck: ReturnType<typeof useDoctorAwayCheck>
  awayWarning: string
  removal: RemovalConfirm
  close: () => void
  handleSave: (force?: boolean, awayAccepted?: boolean, removalAccepted?: boolean) => unknown
  isSeries: boolean
  saveSeries: (removalAccepted: boolean) => void
}

// Every secondary dialog's props, so the main dialog stays a layout.
export const useDialogModalsProps = (args: Args): ModalsProps => {
  const { patientPicker, actions, awayCheck, editedPatientData, close, handleSave } = args
  // The removal prompt resumes whichever save asked it.
  const resumeRemoval = args.isSeries
    ? () => args.saveSeries(true)
    : () => void handleSave(false, false, true)
  return {
    languages: args.languages,
    isAddPatientOpen: args.isAddPatientOpen,
    isPatientSearchOpen: patientPicker.isSearchOpen,
    isNotifyOpen: args.isNotifyOpen,
    isDeleteOpen: args.isDeleteOpen,
    isDeleting: actions.isDeleting,
    isEncounterOpen: args.isEncounterOpen,
    isResolvingEncounter: actions.isResolvingEncounter,
    encounterPatientId: editedPatientData?.patient?.id,
    selectedEncounterId: actions.mappedEncounterId ?? editedPatientData?.emr_encounter_id ?? null,
    encounterError: actions.encounterError,
    overlapConfirmOpen: args.isOverlapOpen,
    onCloseAddPatient: () => args.setIsAddPatientOpen(false),
    onSubmitPatient: patientPicker.create,
    onClosePatientSearch: () => patientPicker.setIsSearchOpen(false),
    onSelectPatient: patientPicker.select,
    onCloseNotify: () => args.setIsNotifyOpen(false),
    onConfirmNotify: async () => {
      args.setIsNotifyOpen(false)
      await actions.notify()
    },
    onCancelDelete: () => args.setIsDeleteOpen(false),
    onConfirmDelete: async (sendSms: boolean) => {
      const deleted = await actions.remove(sendSms)
      args.setIsDeleteOpen(false)
      if (deleted) close()
    },
    onCloseEncounter: () => args.setIsEncounterOpen(false),
    onConfirmEncounter: async (params) => {
      const mapped = await actions.mapEncounter(params)
      if (mapped) args.setIsEncounterOpen(false)
    },
    isManualCheckInOpen: args.isManualCheckInOpen,
    onCloseManualCheckIn: () => args.setIsCheckInOpen(false),
    onConfirmManualCheckIn: async () => {
      args.setIsCheckInOpen(false)
      if (await actions.checkIn()) close()
    },
    awayConfirmOpen: awayCheck.isConfirmOpen,
    awayWarning: args.awayWarning,
    removalConfirm: args.removal.modalProps(resumeRemoval),
    onCloseAwayConfirm: awayCheck.cancel,
    onConfirmAway: () => awayCheck.accept(() => void handleSave(false, true)),
    onCloseOverlapConfirm: () => args.setPendingOverlapSave(false),
    onConfirmOverlap: () => {
      args.setPendingOverlapSave(false)
      void handleSave(true)
    },
  }
}
